import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWallet } from '@solana/wallet-adapter-react'
import { RetroWindow, RetroButton, PixelText } from '@/components/retro'
import { useGameStore, useWalletStore } from '@/store'

export const SettingsScreen: FC = () => {
  const navigate = useNavigate()
  const { disconnect } = useWallet()
  const { publicKey } = useWalletStore()
  const {
    crtEffectEnabled,
    soundEnabled,
    decisionTimerEnabled,
    voiceModeEnabled,
    toggleCRTEffect,
    toggleSound,
    toggleDecisionTimer,
    toggleVoiceMode,
    resetGame,
  } = useGameStore()

  const handleDisconnectWallet = async () => {
    await disconnect()
    resetGame()
    navigate('/')
  }

  const handleResetGame = () => {
    if (confirm('Are you sure you want to reset your game? This cannot be undone.')) {
      resetGame()
      navigate('/')
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <RetroWindow title="SETTINGS" className="w-full max-w-md">
        <div className="space-y-6">
          {/* Display Settings */}
          <div>
            <PixelText size="sm" color="accent" className="mb-3 block">
              DISPLAY
            </PixelText>

            <div className="space-y-3">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="font-retro text-lg text-game-text">
                  CRT Effect
                </span>
                <button
                  onClick={toggleCRTEffect}
                  className={`
                    w-16 h-8 rounded-sm border-2 border-game-border
                    transition-colors relative
                    ${crtEffectEnabled ? 'bg-game-accent' : 'bg-game-panel'}
                  `}
                >
                  <span
                    className={`
                      absolute top-1 w-5 h-5 bg-game-text rounded-sm
                      transition-transform
                      ${crtEffectEnabled ? 'left-9' : 'left-1'}
                    `}
                  />
                </button>
              </label>

              <label className="flex items-center justify-between cursor-pointer">
                <span className="font-retro text-lg text-game-text">
                  Sound Effects
                </span>
                <button
                  onClick={toggleSound}
                  className={`
                    w-16 h-8 rounded-sm border-2 border-game-border
                    transition-colors relative
                    ${soundEnabled ? 'bg-game-accent' : 'bg-game-panel'}
                  `}
                >
                  <span
                    className={`
                      absolute top-1 w-5 h-5 bg-game-text rounded-sm
                      transition-transform
                      ${soundEnabled ? 'left-9' : 'left-1'}
                    `}
                  />
                </button>
              </label>
            </div>
          </div>

          {/* Gameplay Settings */}
          <div className="pt-4 border-t border-game-border">
            <PixelText size="sm" color="accent" className="mb-3 block">
              GAMEPLAY
            </PixelText>

            <div className="space-y-3">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="font-retro text-lg text-game-text block">
                    Decision Timer
                  </span>
                  <span className="font-retro text-xs text-game-muted">
                    30 second countdown for choices
                  </span>
                </div>
                <button
                  onClick={toggleDecisionTimer}
                  className={`
                    w-16 h-8 rounded-sm border-2 border-game-border
                    transition-colors relative
                    ${decisionTimerEnabled ? 'bg-game-accent' : 'bg-game-panel'}
                  `}
                >
                  <span
                    className={`
                      absolute top-1 w-5 h-5 bg-game-text rounded-sm
                      transition-transform
                      ${decisionTimerEnabled ? 'left-9' : 'left-1'}
                    `}
                  />
                </button>
              </label>

              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="font-retro text-lg text-game-text block">
                    Voice Mode
                  </span>
                  <span className="font-retro text-xs text-game-muted">
                    TTS narration + speech input
                  </span>
                </div>
                <button
                  onClick={toggleVoiceMode}
                  className={`
                    w-16 h-8 rounded-sm border-2 border-game-border
                    transition-colors relative
                    ${voiceModeEnabled ? 'bg-game-accent' : 'bg-game-panel'}
                  `}
                >
                  <span
                    className={`
                      absolute top-1 w-5 h-5 bg-game-text rounded-sm
                      transition-transform
                      ${voiceModeEnabled ? 'left-9' : 'left-1'}
                    `}
                  />
                </button>
              </label>
            </div>
          </div>

          {/* Account Settings */}
          <div className="pt-4 border-t border-game-border">
            <PixelText size="sm" color="accent" className="mb-3 block">
              ACCOUNT
            </PixelText>

            {publicKey ? (
              <div className="space-y-3">
                <div className="p-2 bg-black border border-game-border">
                  <span className="font-retro text-sm text-game-muted">
                    Connected Wallet:
                  </span>
                  <p className="font-mono text-xs text-game-text truncate">
                    {publicKey}
                  </p>
                </div>

                <RetroButton
                  variant="default"
                  className="w-full"
                  onClick={handleDisconnectWallet}
                >
                  DISCONNECT WALLET
                </RetroButton>
              </div>
            ) : (
              <p className="font-retro text-game-muted">
                No wallet connected
              </p>
            )}
          </div>

          {/* Social Connections */}
          <div className="pt-4 border-t border-game-border">
            <PixelText size="sm" color="accent" className="mb-3 block">
              CONNECTIONS
            </PixelText>

            <RetroButton variant="default" className="w-full" disabled>
              CONNECT TWITTER (Coming Soon)
            </RetroButton>
          </div>

          {/* Danger Zone */}
          <div className="pt-4 border-t border-game-border">
            <PixelText size="sm" color="loss" className="mb-3 block">
              DANGER ZONE
            </PixelText>

            <RetroButton
              variant="danger"
              className="w-full"
              onClick={handleResetGame}
            >
              RESET GAME DATA
            </RetroButton>
          </div>

          {/* Back Button */}
          <div className="pt-4">
            <RetroButton
              variant="primary"
              className="w-full"
              onClick={() => navigate('/')}
            >
              BACK TO MENU
            </RetroButton>
          </div>
        </div>
      </RetroWindow>

      {/* Version Info */}
      <div className="mt-4 text-center">
        <p className="font-retro text-sm text-game-muted">
          Trench Quest v0.1.0
        </p>
        <p className="font-retro text-xs text-game-muted mt-1">
          Built with React + Vite + Firebase
        </p>
      </div>
    </div>
  )
}
